import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import cogoToast from '@successtar/cogo-toast'
import Button from '@renderer/components/Button'
import { capabilities, getPreloadApi, openExternalUrl } from '@renderer/lib/runtime'

interface CameraGuideModalProps {
  isOpen: boolean
  onClose: () => void
  isElectron: boolean
  onFetchDevices: () => Promise<void>
}

export const CameraGuideModal: React.FC<CameraGuideModalProps> = ({
  isOpen,
  onClose,
  isElectron,
  onFetchDevices
}) => {
  const { t } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isStoreInstall, setIsStoreInstall] = useState(false)

  useEffect(() => {
    const checkStoreInstall = async (): Promise<void> => {
      const api = getPreloadApi()
      if (api?.getPrivilegeRestrictions) {
        try {
          const restrictions = await api.getPrivilegeRestrictions()
          setIsStoreInstall(restrictions.isStoreInstall)
        } catch (error) {
          console.error('Failed to check privilege restrictions:', error)
        }
      }
    }
    checkStoreInstall()
  }, [])

  if (!isOpen) return null

  const mobile = capabilities.isMobile

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 z-50 ${
        mobile ? 'flex flex-col justify-end' : 'flex items-center justify-center'
      }`}
    >
      <div
        className={`bg-white w-full mx-0 max-w-xl sm:mx-4 sm:rounded-lg ${
          mobile
            ? 'rounded-t-2xl max-h-[92dvh] overflow-y-auto overscroll-contain p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]'
            : 'p-8 rounded-lg'
        }`}
      >
        <h2 className="text-2xl font-bold mb-6">{t('cameraGuide.title')}</h2>

        <div className="space-y-6">
          {/* Step 1: Connect Camera */}
          <div className="space-y-2">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm">
                1
              </span>
              {t('cameraGuide.step1.title')}
            </h3>
            <div className="ml-8 text-sm text-gray-600">
              • {t('cameraGuide.step1.bullet1')}
              <br />
              • {t('cameraGuide.step1.bullet2')}
              <br />• {t('cameraGuide.step1.bullet3')}
            </div>
          </div>

          {/* Step 2: Install Driver */}
          <div className="space-y-2">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm">
                2
              </span>
              {t('cameraGuide.step2.title')}
            </h3>
            <div className="ml-8 space-y-4">
              {isStoreInstall ? (
                <div className="p-4 border border-yellow-300 bg-yellow-50 rounded-md">
                  <p className="text-sm text-yellow-800 mb-2">
                    {t('cameraGuide.step2.storeWarning')}
                  </p>
                  <a
                    href="https://dreamboothstudio.com/docs/windows-store-driver-installation"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-yellow-800 underline font-medium"
                  >
                    {t('cameraGuide.step2.viewManualInstructions')}
                  </a>
                </div>
              ) : (
                <>
                  <div className="text-sm text-gray-600">
                    {isElectron
                      ? t('cameraGuide.step2.electronInstruction')
                      : t('cameraGuide.step2.webInstruction')}
                  </div>
                  <Button
                    text={
                      isProcessing
                        ? t('cameraGuide.step2.processing')
                        : isElectron
                          ? t('cameraGuide.step2.openZadig')
                          : t('cameraGuide.step2.downloadZadig')
                    }
                    onClick={async () => {
                      setIsProcessing(true)
                      try {
                        if (isElectron && typeof window.api?.openZadig === 'function') {
                          await window.api.openZadig()
                        } else if (!isElectron) {
                          const response = await fetch('/assets/zadig.exe')
                          const blob = await response.blob()
                          const url = window.URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = 'zadig.exe'
                          document.body.appendChild(a)
                          a.click()
                          a.remove()
                          window.URL.revokeObjectURL(url)
                        } else {
                          cogoToast.error(t('cameraGuide.step2.failedToOpen', 'Zadig is not available'))
                        }
                      } catch (err: unknown) {
                        console.error(err)
                        const errorMessage =
                          err instanceof Error
                            ? err.message
                            : t('cameraGuide.step2.failedToOpen')
                        cogoToast.error(errorMessage)
                        if (errorMessage.includes('Instructions:')) {
                          const instructionsUrl = errorMessage.split('Instructions: ')[1]
                          if (instructionsUrl) {
                            void openExternalUrl(instructionsUrl)
                          }
                        }
                      }
                      setIsProcessing(false)
                    }}
                    disabled={isProcessing}
                    className="!px-6 !py-2 !text-base"
                  />
                </>
              )}
            </div>
          </div>

          {/* Step 3: Zadig Instructions */}
          <div className="space-y-2">
            <h3 className="text-lg font-medium flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm">
                3
              </span>
              {t('cameraGuide.step3.title')}
            </h3>
            <div className="ml-8 text-sm text-gray-600">
              <ol className="list-decimal pl-5 space-y-1">
                <li>{t('cameraGuide.step3.instruction1')}</li>
                <li>{t('cameraGuide.step3.instruction2')}</li>
                <li>{t('cameraGuide.step3.instruction3')}</li>
                <li>{t('cameraGuide.step3.instruction4')}</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            text={t('cameraGuide.buttons.close')}
            variant="secondary"
            onClick={onClose}
            className="!px-6 !py-2 !text-base"
          />
          <Button
            text={t('cameraGuide.buttons.done')}
            onClick={async () => {
              await onFetchDevices()
              onClose()
            }}
            className="!px-6 !py-2 !text-base"
          />
        </div>
      </div>
    </div>
  )
}
